import { Component, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MonacoEditorLanguage } from '../../../components/monaco-editor/monaco-editor.component';
import { CustomCodeTemplateParseError, findCustomCodeTemplateParseError } from './custom-code-template-validation';
import { CustomCodeVariableDoc } from './custom-code.model';

export interface CustomCodeDialogData {
  code: string;
  defaultCode?: string;
  previousCode?: string;
  variables?: CustomCodeVariableDoc[];
  language?: MonacoEditorLanguage;
  /** defaults to true for 'html'; raw-markup surfaces must pass false or a literal `{{` blocks save */
  validateHandlebarsOnSave?: boolean;
}

export interface CustomCodeDialogResult {
  code: string;
}

// shared dialog sizing, one source of truth for every place that opens the editor
export const CUSTOM_CODE_DIALOG_CONFIG = { width: '1024px', maxWidth: '95vw' } as const;

@Component({
  selector: 'smart-cart-form-custom-code-dialog',
  templateUrl: './custom-code-dialog.component.html',
  styleUrls: ['./custom-code-dialog.component.scss'],
  standalone: false,
})
export class SmartCartFormCustomCodeDialogComponent {
  readonly codeControl: FormControl<string>;
  readonly defaultCode: string;
  readonly previousCode: string;
  readonly variables: CustomCodeVariableDoc[];
  readonly language: MonacoEditorLanguage;
  readonly shouldValidateHandlebarsOnSave: boolean;

  isVariableLibraryOpen = false;
  canRevertToPreviousVersion: boolean;
  templateParseError: CustomCodeTemplateParseError | null = null;
  templateParseErrorLocationLabel = '';

  constructor(
    private readonly dialogRef: MatDialogRef<SmartCartFormCustomCodeDialogComponent, CustomCodeDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) data: CustomCodeDialogData,
  ) {
    this.codeControl = new FormControl(data?.code ?? '', { nonNullable: true });
    this.defaultCode = data?.defaultCode ?? '';
    this.previousCode = data?.previousCode ?? '';
    this.variables = data?.variables ?? [];
    this.language = data?.language ?? 'html';
    this.shouldValidateHandlebarsOnSave = data?.validateHandlebarsOnSave ?? this.language === 'html';
    this.canRevertToPreviousVersion = this.hasPreviousVersionDifferentFrom(this.codeControl.value);
  }

  onCodeChange(nextCode: string): void {
    this.codeControl.setValue(nextCode);
    this.canRevertToPreviousVersion = this.hasPreviousVersionDifferentFrom(nextCode);
    this.templateParseError = null;
    this.templateParseErrorLocationLabel = '';
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  resetToDefault(): void {
    this.onCodeChange(this.defaultCode);
  }

  revertToPreviousVersion(): void {
    this.onCodeChange(this.previousCode);
  }

  save(): void {
    this.templateParseError = this.shouldValidateHandlebarsOnSave
      ? findCustomCodeTemplateParseError(this.codeControl.value)
      : null;
    this.templateParseErrorLocationLabel = buildTemplateParseErrorLocationLabel(this.templateParseError);
    if (this.templateParseError) return;
    this.dialogRef.close({ code: this.codeControl.value });
  }

  toggleVariableLibrary(): void {
    this.isVariableLibraryOpen = !this.isVariableLibraryOpen;
  }

  copyVariable(reference: string): void {
    // let a text selection win over the click-to-copy
    const selection = window.getSelection?.()?.toString() ?? '';
    if (selection.length > 0) return;
    navigator.clipboard.writeText(reference);
    this.isVariableLibraryOpen = false;
  }

  private hasPreviousVersionDifferentFrom(code: string): boolean {
    return this.previousCode.length > 0 && this.previousCode !== code;
  }
}

function buildTemplateParseErrorLocationLabel(parseError: CustomCodeTemplateParseError | null): string {
  if (parseError?.lineNumber == null) return '';
  const { lineNumber, column } = parseError;
  return column == null ? ` (line ${lineNumber})` : ` (line ${lineNumber}, column ${column})`;
}
